<template>
  <div class="page-card">
    <div class="section-header">
      <div class="section-title">休息时段配置</div>
      <div class="section-actions">
        <select
          v-model="selectedChairId"
          class="tech-select"
          :disabled="techniciansLoading"
        >
          <option value="">
            {{ techniciansLoading ? "加载技师中..." : "选择技师..." }}
          </option>
          <option
            v-for="tech in technicians"
            :key="tech.chairId"
            :value="tech.chairId"
          >
            {{ tech.label }}
          </option>
        </select>
        <button
          class="save-btn"
          type="button"
          :disabled="saving || scheduleLoading || !canSave"
          @click="saveRestSchedule"
        >
          {{ saving ? "保存中..." : "保存休息时段" }}
        </button>
      </div>
    </div>

    <div v-if="!selectedChairId" class="schedule-empty">
      请先选择技师，再配置休息时段
    </div>

    <div v-else class="schedule-layout">
      <div class="calendar-panel">
        <el-calendar v-model="selectedDate" class="rest-calendar">
          <template slot="dateCell" slot-scope="{ date, data }">
            <div
              class="calendar-day"
              :class="{
                'calendar-day--selected':
                  isSameDate(date, selectedDate) && !isPastDate(date),
                'calendar-day--disabled': isPastDate(date),
              }"
              @click.stop="onCalendarDateClick(date)"
            >
              <span class="calendar-day__num">
                {{ data.day.split("-").slice(2).join("") }}
              </span>
              <span
                v-if="hasRestOnDate(data.day)"
                class="calendar-day__dot"
              ></span>
            </div>
          </template>
        </el-calendar>
      </div>

      <div class="slots-panel">
        <div class="slots-panel-header">
          <div class="slots-date">{{ formattedSelectedDate }}</div>
          <div class="slots-hint">选择当日的休息时段</div>
          <div class="rest-quick-actions">
            <button
              type="button"
              class="rest-quick-btn"
              :disabled="scheduleLoading"
              @click="applyRestPreset('fullDay')"
            >
              休全天
            </button>
            <button
              type="button"
              class="rest-quick-btn"
              :disabled="scheduleLoading"
              @click="applyRestPreset('morning')"
            >
              休上午（8:00-12:00）
            </button>
            <button
              type="button"
              class="rest-quick-btn"
              :disabled="scheduleLoading"
              @click="applyRestPreset('afternoon')"
            >
              休下午（13:00-17:00）
            </button>
            <button
              type="button"
              class="rest-quick-btn rest-quick-btn--clear"
              :disabled="
                scheduleLoading ||
                clearing ||
                !selectedChairId ||
                !restSlotHours.length
              "
              @click="clearDayRestSlots"
            >
              {{ clearing ? "清空中..." : "清空当日休息时段" }}
            </button>
          </div>
        </div>

        <div v-if="scheduleLoading" class="slots-loading">休息时段加载中...</div>
        <template v-else>
          <div class="time-slots">
            <button
              v-for="slot in timeSlots"
              :key="slot.hour"
              type="button"
              class="time-slot"
              :class="{ 'time-slot--selected': isRestSlot(slot.hour) }"
              @click="toggleRestSlot(slot.hour)"
            >
              {{ slot.label }}
            </button>
          </div>

          <div class="legend">
            <div class="legend-item">
              <span class="legend-box legend-box-work"></span>
              <span>工作时段</span>
            </div>
            <div class="legend-item">
              <span class="legend-box legend-box-rest"></span>
              <span>休息时段</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot legend-dot--marked"></span>
              <span>当日有休息</span>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { clearSchedule, getSchedule, saveSchedule } from "@/api/schedule";
import { listTechniciansSimple } from "@/api/technician";

const SUCCESS_CODE = 20000;
const DEFAULT_WORK_START_HOUR = 8;
const DEFAULT_WORK_END_HOUR = 18;

const REST_PRESET_HOURS = {
  fullDay: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
  morning: [8, 9, 10, 11, 12],
  afternoon: [13, 14, 15, 16, 17],
};

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDateKey(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatDateDisplay(dateKey) {
  if (!dateKey) return "";
  const parts = dateKey.split("-");
  if (parts.length !== 3) return dateKey;
  return `${parts[0]}年${Number(parts[1])}月${Number(parts[2])}日`;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getToday() {
  return startOfDay(new Date());
}

function buildTimeSlots(startHour, endHour) {
  const start = Number(startHour);
  const end = Number(endHour);
  if (Number.isNaN(start) || Number.isNaN(end) || start >= end) {
    return buildTimeSlots(DEFAULT_WORK_START_HOUR, DEFAULT_WORK_END_HOUR);
  }
  const slots = [];
  for (let hour = start; hour < end; hour += 1) {
    slots.push({
      hour,
      label: `${pad2(hour)}:00`,
    });
  }
  return slots;
}

function parseSlotHours(item, startHour, endHour) {
  if (!item || !Array.isArray(item.slots)) return [];

  const start = Number(startHour ?? DEFAULT_WORK_START_HOUR);
  const end = Number(endHour ?? DEFAULT_WORK_END_HOUR);

  return [
    ...new Set(
      item.slots
        .map((slot) => Number(slot.slotHour))
        .filter((hour) => !Number.isNaN(hour) && hour >= start && hour < end)
    ),
  ].sort((a, b) => a - b);
}

export default {
  name: "SchedulePage",
  data() {
    return {
      saving: false,
      clearing: false,
      scheduleLoading: false,
      techniciansLoading: false,
      selectedChairId: "",
      selectedDate: new Date(),
      technicians: [],
      timeSlots: buildTimeSlots(
        DEFAULT_WORK_START_HOUR,
        DEFAULT_WORK_END_HOUR
      ),
      restSlotHours: [],
      userTouchedSlots: false,
      restScheduleDates: {},
      scheduleByDate: {},
    };
  },
  computed: {
    scheduleDate() {
      return formatDateKey(this.selectedDate);
    },
    formattedSelectedDate() {
      return formatDateDisplay(this.scheduleDate);
    },
    hasLoadedRestOnSelectedDay() {
      const hours = this.scheduleByDate[this.scheduleDate];
      return Array.isArray(hours) && hours.length > 0;
    },
    canSave() {
      if (!this.selectedChairId || !this.scheduleDate) return false;
      if (this.restSlotHours.length === 0) return false;

      return this.userTouchedSlots || this.hasLoadedRestOnSelectedDay;
    },
  },
  watch: {
    selectedChairId(val) {
      this.restScheduleDates = {};
      this.scheduleByDate = {};
      if (val) {
        this.initSchedule();
      } else {
        this.resetRestSlots();
      }
    },
    selectedDate(val) {
      if (this.isPastDate(val)) {
        const today = getToday();
        if (formatDateKey(val) !== formatDateKey(today)) {
          this.selectedDate = today;
        }
        return;
      }
      if (this.selectedChairId) {
        this.loadDaySchedule();
      }
    },
  },
  mounted() {
    this.fetchTechnicians();
  },
  methods: {
    showMessage(type, text) {
      this.$message({ message: text, type });
    },
    isBizError(res) {
      return res && res.code != null && Number(res.code) !== SUCCESS_CODE;
    },
    isSameDate(a, b) {
      return formatDateKey(a) === formatDateKey(b);
    },
    isPastDate(date) {
      return startOfDay(date).getTime() < getToday().getTime();
    },
    onCalendarDateClick(date) {
      if (this.isPastDate(date)) return;
      this.selectedDate = startOfDay(date);
    },
    hasRestOnDate(dateKey) {
      const key =
        typeof dateKey === "string" ? dateKey : formatDateKey(dateKey);
      return Boolean(this.restScheduleDates[key]);
    },
    applyScheduleList(list) {
      const byDate = {};
      const markers = {};

      if (Array.isArray(list)) {
        list.forEach((item) => {
          if (!item || !item.scheduleDate) return;
          const startHour = item.workStartHour ?? DEFAULT_WORK_START_HOUR;
          const endHour = item.workEndHour ?? DEFAULT_WORK_END_HOUR;
          const hours = parseSlotHours(item, startHour, endHour);
          byDate[item.scheduleDate] = hours;
          if (hours.length > 0) {
            markers[item.scheduleDate] = true;
          }
        });
      }

      this.scheduleByDate = byDate;
      this.restScheduleDates = markers;
    },
    updateScheduleCache(dateKey, hours) {
      const nextByDate = { ...this.scheduleByDate, [dateKey]: [...hours] };
      const nextMarkers = { ...this.restScheduleDates };
      if (hours.length > 0) {
        nextMarkers[dateKey] = true;
      } else {
        delete nextMarkers[dateKey];
      }
      this.scheduleByDate = nextByDate;
      this.restScheduleDates = nextMarkers;
    },
    async initSchedule() {
      const loaded = await this.fetchScheduleList();
      if (loaded) {
        this.loadDaySchedule();
      }
    },
    async fetchScheduleList() {
      if (!this.selectedChairId) return false;

      this.scheduleLoading = true;
      try {
        const res = await getSchedule(this.selectedChairId);
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "休息时段加载失败");
          this.applyScheduleList([]);
          return false;
        }
        this.applyScheduleList(res && res.data);
        return true;
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "休息时段加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.applyScheduleList([]);
        return false;
      } finally {
        this.scheduleLoading = false;
      }
    },
    mapTechnicianOptions(list) {
      if (!Array.isArray(list)) return [];
      return list
        .map((item) => {
          const chairId = String(item.chairId ?? "").trim();
          const workerId = item.workerId || "";
          const realName = item.realName || "";
          if (!chairId) return null;
          const label =
            realName && workerId
              ? `${realName} (${workerId})`
              : realName || workerId;
          return { chairId, workerId, realName, label };
        })
        .filter(Boolean);
    },
    async fetchTechnicians() {
      this.techniciansLoading = true;
      try {
        const res = await listTechniciansSimple();
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "技师列表加载失败");
          this.technicians = [];
          return;
        }
        this.technicians = this.mapTechnicianOptions(res && res.data);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "技师列表加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.technicians = [];
      } finally {
        this.techniciansLoading = false;
      }
    },
    resetRestSlots() {
      this.restSlotHours = [];
      this.userTouchedSlots = false;
      this.timeSlots = buildTimeSlots(
        DEFAULT_WORK_START_HOUR,
        DEFAULT_WORK_END_HOUR
      );
    },
    getSelectedRestSlotHours() {
      const validHours = new Set(this.timeSlots.map((slot) => slot.hour));
      return this.restSlotHours
        .filter((hour) => validHours.has(Number(hour)))
        .sort((a, b) => a - b);
    },
    isRestSlot(hour) {
      return this.restSlotHours.includes(Number(hour));
    },
    toggleRestSlot(hour) {
      const h = Number(hour);
      const next = [...this.restSlotHours];
      const index = next.indexOf(h);
      if (index >= 0) {
        next.splice(index, 1);
      } else {
        next.push(h);
        next.sort((a, b) => a - b);
      }
      this.restSlotHours = next;
      this.userTouchedSlots = true;
    },
    applyRestPreset(presetKey) {
      const presetHours = REST_PRESET_HOURS[presetKey];
      if (!Array.isArray(presetHours)) return;

      const validHours = new Set(this.timeSlots.map((slot) => Number(slot.hour)));
      this.restSlotHours = presetHours
        .filter((hour) => validHours.has(Number(hour)))
        .sort((a, b) => a - b);
      this.userTouchedSlots = true;
    },
    async clearDayRestSlots() {
      if (!this.selectedChairId) {
        this.showMessage("warning", "请选择技师");
        return;
      }
      if (!this.scheduleDate) {
        this.showMessage("warning", "请选择日期");
        return;
      }

      this.clearing = true;
      try {
        const res = await clearSchedule({
          chairId: this.selectedChairId,
          scheduleDate: this.scheduleDate,
        });

        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "清空当日休息时段失败");
          return;
        }

        this.restSlotHours = [];
        this.userTouchedSlots = false;
        this.updateScheduleCache(this.scheduleDate, []);
        this.showMessage("success", (res && res.msg) || "已清空当日休息时段");
      } catch (error) {
        const msg =
          (error && error.msg) ||
          error.message ||
          "清空当日休息时段失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.clearing = false;
      }
    },
    loadDaySchedule() {
      if (!this.selectedChairId || !this.scheduleDate) return;

      this.userTouchedSlots = false;
      this.timeSlots = buildTimeSlots(
        DEFAULT_WORK_START_HOUR,
        DEFAULT_WORK_END_HOUR
      );

      const cachedHours = this.scheduleByDate[this.scheduleDate];
      this.restSlotHours = Array.isArray(cachedHours) ? [...cachedHours] : [];
    },
    async saveRestSchedule() {
      if (!this.selectedChairId) {
        this.showMessage("warning", "请选择技师");
        return;
      }
      if (!this.scheduleDate) {
        this.showMessage("warning", "请选择日期");
        return;
      }

      this.saving = true;
      try {
        const res = await saveSchedule({
          chairId: this.selectedChairId,
          scheduleDate: this.scheduleDate,
          restSlotHours: this.getSelectedRestSlotHours(),
        });

        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "休息时段保存失败");
          return;
        }

        const savedHours = this.getSelectedRestSlotHours();
        this.updateScheduleCache(this.scheduleDate, savedHours);
        this.showMessage("success", (res && res.msg) || "休息时段保存成功");
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "休息时段保存失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.page-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 12px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tech-select {
  min-width: 180px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  background: #fff;
  cursor: pointer;
}

.tech-select:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.save-btn {
  border: none;
  background: #409eff;
  color: #fff;
  border-radius: 4px;
  height: 32px;
  padding: 0 16px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.save-btn:hover:not(:disabled) {
  background: #096dd9;
}

.save-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.schedule-empty {
  padding: 64px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.schedule-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.calendar-panel {
  flex: 0 0 380px;
  max-width: 100%;
  border: 1px solid #eef1f6;
  border-radius: 8px;
  overflow: hidden;
}

.rest-calendar {
  background: #fff;
}

.rest-calendar >>> .el-calendar__header {
  padding: 12px 16px;
  border-bottom: 1px solid #eef1f6;
}

.rest-calendar >>> .el-calendar__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.rest-calendar >>> .el-calendar__body {
  padding: 8px 12px 12px;
}

.rest-calendar >>> .el-calendar-table thead th {
  color: #909399;
  font-weight: 500;
  padding: 8px 0;
}

.rest-calendar >>> .el-calendar-table td {
  border: none;
}

.rest-calendar >>> .el-calendar-day {
  height: 48px;
  padding: 4px;
}

.calendar-day {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  transition: background 0.15s, color 0.15s;
}

.calendar-day__num {
  line-height: 1.2;
}

.calendar-day__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #52c41a;
  flex-shrink: 0;
}

.calendar-day--selected {
  background: #409eff;
  color: #fff;
  font-weight: 600;
}

.calendar-day--selected .calendar-day__dot {
  background: #b7eb8f;
}

.calendar-day--disabled {
  color: #c0c4cc;
  cursor: not-allowed;
  background: #f5f7fa;
}

.rest-calendar >>> .prev .calendar-day:not(.calendar-day--disabled),
.rest-calendar >>> .next .calendar-day:not(.calendar-day--disabled) {
  color: #c0c4cc;
}

.slots-panel {
  flex: 1;
  min-width: 0;
  border: 1px solid #eef1f6;
  border-radius: 8px;
  padding: 20px;
}

.slots-panel-header {
  margin-bottom: 20px;
}

.slots-date {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.slots-hint {
  font-size: 13px;
  color: #909399;
}

.rest-quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.rest-quick-btn {
  height: 32px;
  padding: 0 14px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}

.rest-quick-btn:hover:not(:disabled) {
  color: #409eff;
  border-color: #409eff;
  background: #f5f9ff;
}

.rest-quick-btn--clear {
  color: #cf1322;
  border-color: #ffa39e;
  background: #fff1f0;
}

.rest-quick-btn--clear:hover:not(:disabled) {
  color: #a8071a;
  border-color: #ff7875;
  background: #fff1f0;
}

.rest-quick-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.slots-loading {
  padding: 48px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.time-slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 12px;
}

.time-slot {
  height: 44px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  color: #606266;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.time-slot:hover {
  border-color: #409eff;
  color: #409eff;
  background: #f5f9ff;
}

.time-slot--selected {
  border-color: #409eff;
  background: #409eff;
  color: #fff;
  font-weight: 500;
}

.time-slot--selected:hover {
  background: #096dd9;
  border-color: #096dd9;
  color: #fff;
}

.legend {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eef1f6;
  font-size: 13px;
  color: #5c6b7f;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.legend-box {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  flex-shrink: 0;
}

.legend-box-work {
  background: #fff;
  border: 1px solid #dcdfe6;
}

.legend-box-rest {
  background: #409eff;
  border: 1px solid #409eff;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-dot--marked {
  background: #52c41a;
}

@media (max-width: 900px) {
  .schedule-layout {
    flex-direction: column;
  }

  .calendar-panel {
    flex: none;
    width: 100%;
  }
}
</style>
